import React, { useState, useMemo } from 'react';
import DSBox from '../../../../design-system/DSBox';
import DSLabelTypography from '../../../../design-system/Typography/DSLabelTypography';
import DSIconButton from '../../../../design-system/DSIconButton';
import DSHelpOutline from '../../../../design-system/DSHelpOutline';
import DSTooltip from '../../../../design-system/DSTooltip';
import DSCircularProgress from '../../../../design-system/DSCircularProgress';
import { useTheme } from '@mui/material';
import { ProductMockTemplate } from '../../../../api/productMockTemplates';
import useProductMockTemplatesQuery from '../../hooks/useProductMockTemplatesQuery';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SelectItemModal from './SelectItemModal';
import MockupSelectCard from './MockupSelectCard';
import { COLLECTION_SCENE_SPEC } from '../../../../consts/specs';
import { getScaledCloudinaryUrl } from '../../../../utils';

type CollectionMockupSelectorProps = {
  selectedMockTemplateId: string;
  onMockTemplateIdChange: (templateId: string) => void;
};

const CollectionMockupSelector = ({
  selectedMockTemplateId,
  onMockTemplateIdChange,
}: CollectionMockupSelectorProps) => {
  const { spacing, palette } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: templatesData, isLoading } = useProductMockTemplatesQuery();
  const mockTemplates = templatesData?.data || [];

  const selectedTemplate = mockTemplates.find(
    (template) => template.id === selectedMockTemplateId,
  );

  const scaledSelectedPreviewUrl = useMemo(
    () =>
      selectedTemplate
        ? getScaledCloudinaryUrl(selectedTemplate.previewImageUrl, 100)
        : '',
    [selectedTemplate],
  );

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMockTemplateIdChange('');
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmSelection = (selectedIds: string[]) => {
    onMockTemplateIdChange(selectedIds[0] || '');
  };

  const renderMockupItem = (
    mockup: ProductMockTemplate,
    isSelected: boolean,
    onToggle: () => void,
  ) => (
    <MockupSelectCard
      mockup={mockup}
      isSelected={isSelected}
      onToggle={onToggle}
    />
  );

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          <DSLabelTypography>Scene</DSLabelTypography>
          <DSTooltip title={COLLECTION_SCENE_SPEC}>
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: 'color 0.3s ease',
              }}
            >
              <DSHelpOutline sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>
        <DSBox
          sx={{
            display: 'flex',
            height: '74px',
            padding: '16px 32px',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: '16px',
            backgroundColor: palette.paper.elevation3,
          }}
        >
          <DSCircularProgress size={24} />
        </DSBox>
      </DSBox>
    );
  }

  return (
    <>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          <DSLabelTypography>Scene</DSLabelTypography>
          <DSTooltip title={COLLECTION_SCENE_SPEC}>
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: 'color 0.3s ease',
              }}
            >
              <DSHelpOutline sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>

        {selectedTemplate ? (
          <DSBox
            onClick={handleOpenModal}
            sx={{
              display: 'flex',
              height: '74px',
              padding: '16px 20px',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderRadius: '16px',
              backgroundColor: palette.paper.elevation3,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: palette.paper.elevation6,
              },
            }}
          >
            <DSBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing(1.5),
                flex: 1,
              }}
            >
              <img
                src={scaledSelectedPreviewUrl}
                alt={selectedTemplate.fileName}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '4px',
                  objectFit: 'cover',
                }}
              />
              <DSLabelTypography>{selectedTemplate.fileName}</DSLabelTypography>
            </DSBox>
            <DSIconButton
              onClick={handleClearSelection}
              sx={{
                height: spacing(3),
                width: spacing(3),
                color: palette.text.secondary,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </DSIconButton>
          </DSBox>
        ) : (
          <DSBox
            onClick={handleOpenModal}
            sx={{
              display: 'flex',
              height: '74px',
              padding: '16px 32px',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderRadius: '16px',
              backgroundColor: palette.paper.elevation3,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: palette.paper.elevation6,
              },
            }}
          >
            <DSLabelTypography color="textSecondary">
              Select a scene
            </DSLabelTypography>
            <ArrowDropDownIcon sx={{ color: palette.text.secondary }} />
          </DSBox>
        )}
      </DSBox>

      <SelectItemModal<ProductMockTemplate>
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Select Collection Scene"
        subtitle="Click to choose the scene to display your collection"
        items={mockTemplates}
        getItemId={(mockup) => mockup.id}
        selectedItemIds={selectedMockTemplateId ? [selectedMockTemplateId] : []}
        onConfirm={handleConfirmSelection}
        maxSelection={1}
        renderItem={renderMockupItem}
        gridColumns={3}
      />
    </>
  );
};

export default CollectionMockupSelector;
